import { Link, NavLink, Navigate, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { useProject, useProjectAttention } from '../../api/projects.js';
import type { Project } from '../../api/types.js';
import { Alert, Card, Spinner } from '../../components/ui.js';
import { Stepper, stepForStatus } from '../../components/Stepper.js';
import { ProjectStatusBadge } from '../../status.js';

interface ProjectContext {
  project: Project;
}

/** Nested project pages read the loaded project from here. */
export function useProjectContext(): ProjectContext {
  return useOutletContext<ProjectContext>();
}

/** The same section split the staff console uses, mirrored for the founder. */
const SECTIONS: { to: string; label: string }[] = [
  { to: 'dashboard', label: 'Dashboard' },
  { to: 'questionnaire', label: 'Questionnaire' },
  { to: 'specification', label: 'Specification' },
  { to: 'repository', label: 'Repository' },
  { to: 'livetest', label: 'Live testing' },
  { to: 'stories', label: 'User stories' },
  { to: 'board', label: 'Board' },
  { to: 'messages', label: 'Messages' },
  { to: 'guide', label: 'Help & guide' },
];

/**
 * The founder's project workspace: the process stepper at the top, a left-hand
 * section nav (questionnaire / specification / repository / user stories), and
 * the selected section below via the nested routes.
 */
export function ProjectShell() {
  const { id } = useParams<{ id: string }>();
  const project = useProject(id ?? '');
  const attention = useProjectAttention(id ?? '');

  if (!id) return <Alert>Missing project id.</Alert>;
  if (project.isPending) return <Spinner />;
  if (project.isError || !project.data) return <Alert>Could not load this project.</Alert>;

  const paused = project.data.status === 'archived';

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Link to="/" className="text-sm text-primary underline">
          ← Home
        </Link>
        <h1 className="font-title text-3xl tracking-wide">{project.data.name}</h1>
        <ProjectStatusBadge status={project.data.status} />
      </div>

      {paused ? (
        <Alert kind="info">
          This project is paused. Resume it from your home page to continue.
        </Alert>
      ) : (
        <Card>
          <Stepper current={stepForStatus(project.data.status)} />
        </Card>
      )}

      <div className="flex flex-col gap-6 sm:flex-row">
        <nav className="flex gap-1 overflow-x-auto sm:w-48 sm:flex-col">
          {SECTIONS.map((s) => (
            <NavLink
              key={s.to}
              to={s.to}
              className={({ isActive }) =>
                `flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition ${
                  isActive ? 'bg-primary text-white' : 'hover:bg-neutral'
                }`
              }
            >
              {s.label}
              {s.to === 'messages' && attention.data?.unreadMessages ? (
                <span
                  title="New messages"
                  aria-label="New messages"
                  className="inline-block h-2 w-2 flex-shrink-0 rounded-full bg-amber-400 ring-2 ring-amber-400/30"
                />
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="min-w-0 flex-1">
          <Outlet context={{ project: project.data } satisfies ProjectContext} />
        </div>
      </div>
    </div>
  );
}

/** Index route: send the founder to the stage the project is on. */
export function ProjectIndexRedirect() {
  const { project } = useProjectContext();
  const to =
    project.status === 'questionnaire'
      ? 'questionnaire'
      : project.status === 'spec_approved' || project.status === 'building'
        ? 'dashboard'
        : 'specification';
  return <Navigate to={to} replace />;
}
